import { useMemo } from 'react'

import DownloadIcon from '@mui/icons-material/Download'
import FolderZipIcon from '@mui/icons-material/FolderZip'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useParams } from 'react-router-dom'

import { BookingAddressType } from 'generated/graphql'
import { GetFileName, SaveFile } from 'helpers'
import { DownloadProofOfDeliveryZip } from 'services'

import { DetailedViewHeader } from './detailed-view'

type TrackingAttachment = {
  __typename?: 'TrackingAttachmentDto' | undefined
  fileId: any
  addressId?: any
  file: {
    __typename?: 'File' | undefined
    id: any
    name: string
    originalFileName: string
    absoluteUri: string
    category?: any
  }
}

interface ProofOfDeliveryViewProps {
  onClose: () => void
  booking?: any
  mode?: 'collection' | 'delivery' | 'combined'
  attachments?: TrackingAttachment[]
}

const emptyStateStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: 6,
  px: 3,
  textAlign: 'center',
}

const mobileContainerStyles = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  minHeight: 0,
}

const webContainerStyles = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  minHeight: 0,
}

const scrollContentStyles = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto' as const,
}

const getFileExtension = (fileName: string): string => {
  return fileName.split('.').pop()?.toLowerCase() || ''
}

const isAllowedFileType = (fileName: string): boolean => {
  const ext = getFileExtension(fileName)
  return ['png', 'jpg', 'jpeg', 'pdf'].includes(ext)
}

const isImageFile = (fileName: string): boolean => {
  const ext = getFileExtension(fileName)
  return ['png', 'jpg', 'jpeg'].includes(ext)
}

interface AttachmentItemProps {
  attachment: TrackingAttachment
  onDownload: (url: string) => void
}

type GroupedAddressAttachment = {
  address: any
  addressAttachments: TrackingAttachment[]
}

type DeliveryViewConfig = {
  title: string
  bookingAttachments: TrackingAttachment[]
  groupedAddressAttachments: GroupedAddressAttachment[]
}

const getEmptyStateMessage = (mode: 'collection' | 'delivery' | 'combined'): string => {
  if (mode === 'collection') return 'No proof of collection documents available'
  if (mode === 'delivery') return 'No proof of delivery documents available'
  return 'No combined proof of service documents available'
}

const AttachmentItem = ({ attachment, onDownload }: AttachmentItemProps) => (
  <ListItem
    disablePadding
    secondaryAction={
      <IconButton
        edge='end'
        aria-label='download'
        onClick={() => onDownload(attachment.file.absoluteUri)}>
        <DownloadIcon />
      </IconButton>
    }>
    <ListItemButton onClick={() => onDownload(attachment.file.absoluteUri)}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box
          sx={{
            overflow: 'hidden',
            width: 60,
            height: 60,
            borderRadius: 1,
          }}>
          {isImageFile(attachment.file.originalFileName || attachment.file.name) ? (
            <img
              src={attachment.file.absoluteUri}
              alt={attachment.file.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              loading='lazy'
            />
          ) : (
            <InsertDriveFileIcon sx={{ fontSize: 32, color: '#929292' }} />
          )}
        </Box>
        <ListItemText
          sx={{
            ml: 1,
          }}
          primary={attachment.file.name || attachment.file.originalFileName}
          primaryTypographyProps={{
            sx: {
              fontSize: '16px',
              fontWeight: 500,
              color: '#1D1A33',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Box>
    </ListItemButton>
  </ListItem>
)

interface GroupedAttachmentsProps {
  mode: 'collection' | 'delivery' | 'combined'
  viewConfig: DeliveryViewConfig
  onDownload: (url: string) => void
}

const GroupedAttachments = ({ mode, viewConfig, onDownload }: GroupedAttachmentsProps) => {
  if (mode === 'combined') {
    if (viewConfig.bookingAttachments.length === 0) {
      return (
        <Box sx={emptyStateStyles}>
          <Typography variant='body1' color='text.secondary'>
            {getEmptyStateMessage(mode)}
          </Typography>
        </Box>
      )
    }

    return (
      <List disablePadding>
        {viewConfig.bookingAttachments.map((attachment) => (
          <AttachmentItem key={attachment.fileId} attachment={attachment} onDownload={onDownload} />
        ))}
      </List>
    )
  }

  if (viewConfig.groupedAddressAttachments.length === 0) {
    return (
      <Box sx={emptyStateStyles}>
        <Typography variant='body1' color='text.secondary'>
          {getEmptyStateMessage(mode)}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {viewConfig.groupedAddressAttachments.map(({ address, addressAttachments }) => {
        const name = address.address?.name
        const fullAddress = [
          address.address?.addressLine1,
          address.address?.addressLine2,
          address.address?.city,
          address.address?.postcode,
        ]
          .filter(Boolean)
          .join(', ')

        return (
          <Box key={address.id}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                pt: 1.5,
                pb: 1.5,
                borderBottom: '1px solid #F0EEF8',
                backgroundColor: '#F8F6FC',
              }}>
              <LocationOnIcon sx={{ fontSize: 28, color: '#471D7C', flexShrink: 0 }} />
              <Box>
                {name && (
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 700, color: '#1D1A33', lineHeight: 1.3 }}>
                    {name}
                  </Typography>
                )}
                {fullAddress && (
                  <Typography variant='caption' sx={{ color: '#555', lineHeight: 1.4 }}>
                    {fullAddress}
                  </Typography>
                )}
              </Box>
            </Box>
            <List disablePadding>
              {addressAttachments.map((item) => (
                <AttachmentItem key={item.fileId} attachment={item} onDownload={onDownload} />
              ))}
            </List>
          </Box>
        )
      })}
    </>
  )
}

export const ProofOfDeliveryView = ({
  onClose,
  booking,
  mode = 'delivery',
  attachments = [],
}: ProofOfDeliveryViewProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { trackingHash } = useParams<{ trackingHash: string }>()

  const viewConfig = useMemo(() => {
    const title =
      mode === 'collection'
        ? 'Proof of Collection'
        : mode === 'delivery'
        ? 'Proof of Delivery'
        : mode === 'combined'
        ? 'Combined Proof of Service Documents'
        : 'Documents'

    const files = attachments.filter((attachment) =>
      isAllowedFileType(attachment.file.originalFileName || attachment.file.name),
    )

    if (mode === 'combined') {
      return {
        title,
        bookingAttachments: files,
        groupedAddressAttachments: [],
      }
    }

    const groupAddressType =
      mode === 'collection' ? BookingAddressType.Collection : BookingAddressType.Delivery

    const groupingAddresses = ((booking?.addresses ?? []) as any[])
      .filter((a) => a.type === groupAddressType)
      .sort((a, b) => a.sequenceOrder - b.sequenceOrder)

    const groupedAddressAttachments = groupingAddresses
      .map((address) => ({
        address,
        addressAttachments: files.filter((file) => file.addressId === address.id),
      }))
      .filter((group) => group.addressAttachments.length > 0)

    return {
      title,
      bookingAttachments: [],
      groupedAddressAttachments,
    }
  }, [mode, booking?.addresses, attachments])

  const hasFilesForZip =
    viewConfig.bookingAttachments.length > 0 || viewConfig.groupedAddressAttachments.length > 0

  const handleDownload = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleDownloadZip = async () => {
    if (!hasFilesForZip || !trackingHash) return

    try {
      const response = await DownloadProofOfDeliveryZip(trackingHash, mode)
      const fileName =
        GetFileName(response) || `Booking_${booking?.id || 'Unknown'}_ProofOfDelivery.zip`
      SaveFile(response.data, fileName)
    } catch (error) {
      console.error('Error downloading zip file:', error)
    }
  }

  if (isMobile) {
    return (
      <Box sx={mobileContainerStyles}>
        <DetailedViewHeader onClose={onClose} title={viewConfig.title} />
        <Box sx={scrollContentStyles}>
          <GroupedAttachments mode={mode} viewConfig={viewConfig} onDownload={handleDownload} />
        </Box>
        {hasFilesForZip && (
          <Box sx={{ p: 2, borderTop: '1px solid #E0E0E0', flexShrink: 0 }}>
            <Button
              fullWidth
              variant='contained'
              startIcon={<FolderZipIcon />}
              onClick={handleDownloadZip}
              sx={{
                backgroundColor: '#1D1A33',
                '&:hover': { backgroundColor: '#2D2A43' },
                textTransform: 'none',
                fontWeight: 600,
              }}>
              Download ZIP
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box sx={webContainerStyles}>
      <DetailedViewHeader onClose={onClose} title={viewConfig.title} />
      <Box sx={scrollContentStyles}>
        <GroupedAttachments mode={mode} viewConfig={viewConfig} onDownload={handleDownload} />
      </Box>
      {hasFilesForZip && (
        <Box sx={{ p: 2, borderTop: '1px solid #E0E0E0', flexShrink: 0 }}>
          <Button
            fullWidth
            variant='contained'
            startIcon={<FolderZipIcon />}
            onClick={handleDownloadZip}
            sx={{
              backgroundColor: '#1D1A33',
              '&:hover': { backgroundColor: '#2D2A43' },
              textTransform: 'none',
              fontWeight: 600,
            }}>
            Download ZIP
          </Button>
        </Box>
      )}
    </Box>
  )
}
